'use strict'

// React Hooks
import {useState, useEffect, useReducer} from 'react'

// PWA Hooks
import {useProductSearch} from '@salesforce/commerce-sdk-react'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useEinstein from './use-einstein'

// Helpers
import {useManualLinking} from './use-manual-linking'

// Utils
import {
    isProductEligibleForAutomaticLinking,
    getAutomaticLinkingRefinements,
    determineRecommendationStrategy,
    isThereCrossSellConfiguration
} from '../utils/recommendation-utils'

// Constants
import {
    RESEAU_H,
    TYPE_PROD_BAS,
    PRODUCT_SEARCH_VARIATION_GROUP_REFINEMENT,
    ADD_TO_CART_STRATEGIES
} from '../constants'

/**
 * Custom React hook to generate add-to-cart product recommendations based on the provided product.
 *
 * This hook determines the appropriate recommendation strategy (manual cross-sell, automatic linking, Einstein recommendations, or product search)
 * for a given product and fetches relevant recommendations accordingly. It manages internal state using a reducer to handle different recommendation
 * strategies and tracks loading states for asynchronous operations. The hook integrates with manual linking, Einstein recommendation, and product search hooks
 * to retrieve and process recommendation data, ensuring only orderable products are returned.
 *
 * @param {Object} product - The product object for which to generate add-to-cart recommendations.
 * @returns {Object} An object containing:
 *   @property {Array} recommendationResult - The list of recommended products to display in the add-to-cart modal.
 *   @property {boolean} isRecommendationLoading - Indicates if the recommendations are still being loaded.
 *
 * @example
 * const { recommendationResult, isRecommendationLoading } = useAddToCartRecommendations(product);
 */
export const useAddToCartRecommendations = (product) => {
    const initialRecommendationState = {
        productIdsToBeFetched: [],
        recommendationRefinements: [],
        isProductSearchEnabled: false,
        isEinsteinRecommendationEnabled: false
    }

    /**
     * Reducer function to manage the state of add-to-cart recommendations.
     *
     * Handles actions to set recommendation refinements, enable Einstein recommendations,
     * and update the list of product IDs to be fetched. Returns the updated state based on the action type.
     *
     * @param {Object} state - The current state of the recommendations.
     * @param {Object} action - The action object containing type and payload.
     * @param {string} action.type - The type of action to perform.
     * @param {*} [action.payload] - The payload associated with the action.
     * @returns {Object} The new state after applying the specified action.
     */
    const recommendationReducer = (state, action) => {
        switch (action.type) {
            case 'SET_RECOMMENDATION_REFINEMENTS':
                return {
                    ...state,
                    recommendationRefinements: action.payload,
                    isProductSearchEnabled: true
                }
            case 'ENABLE_EINSTEIN_RECOMMENDATION':
                return {
                    ...state,
                    isEinsteinRecommendationEnabled: true
                }
            case 'SET_PRODUCT_IDS_TO_BE_FETCHED':
                return {
                    ...state,
                    productIdsToBeFetched: action.payload
                }
            default:
                return state
        }
    }

    const [recommendationState, dispatch] = useReducer(
        recommendationReducer,
        initialRecommendationState
    )

    const [addToCartRecommendationsResult, setAddToCartRecommendationsResult] = useState({
        recommendationResult: [],
        isRecommendationLoading: true
    })

    const {
        isLoading: isEinsteinRecsLoading,
        recommendations: einsteinRecommendations,
        getRecommendations: getEinsteinRecommendations
    } = useEinstein()

    const {manualLinkingResult, isManualLinkingLoading} = useManualLinking(
        product,
        'c_perfectYourLook'
    )

    useEffect(() => {
        if (product) {
            const recommendationStrategy = determineRecommendationStrategy(product)

            switch (recommendationStrategy) {
                // the cross-sell rules for women bikini and men swimshorts (e.g FLRCH144-195, FZRBH105-188)
                case ADD_TO_CART_STRATEGIES.WOMEN_BIKINI:
                    // apply women bikini recommendations
                    if (isThereCrossSellConfiguration(product)) {
                        if (manualLinkingResult?.length > 0) {
                            // if there is a cross-sell configuration apply cross-sell recommendation (e.g FZRBH105-188)
                            setAddToCartRecommendationsResult((prevState) => ({
                                ...prevState,
                                recommendationResult: manualLinkingResult,
                                isRecommendationLoading: false
                            }))
                        }
                    } else if (isProductEligibleForAutomaticLinking(product)) {
                        // Ensure that product search is only made when manual linking check is completed
                        // if there is no cross-sell configuration then use automatic linking rules (e.g FLRCH144-195)
                        dispatch({
                            type: 'SET_RECOMMENDATION_REFINEMENTS',
                            payload: getAutomaticLinkingRefinements(product)
                        })
                    }
                    break
                case ADD_TO_CART_STRATEGIES.MEN_SWIMSHORTS:
                    // apply men swimshort recommendations
                    if (isThereCrossSellConfiguration(product)) {
                        if (manualLinkingResult?.length > 0) {
                            // if there is a cross-sell configuration apply cross-sell recommendation (e.g MISC3C01-167)
                            setAddToCartRecommendationsResult((prevState) => ({
                                ...prevState,
                                recommendationResult: manualLinkingResult,
                                isRecommendationLoading: false
                            }))
                        }
                    } else {
                        // if there is no cross-sell configuration (e.g MTACF510-390), use einstein recommendations with the rule 'added-to-cart-modal-cross-sell'
                        getEinsteinRecommendations('added-to-cart-modal-cross-sell', [product])
                        dispatch({type: 'ENABLE_EINSTEIN_RECOMMENDATION'})
                    }
                    break
                case ADD_TO_CART_STRATEGIES.LIGNE_PRODUIT:
                    /*
                        for products with orliweb_code_ligne_produit “POL” or “CHE”, (e.g CCAC3V00-159)
                        get the products with orliweb_reseau “H” + orliweb_stat10_type_prod “BOTTOM” + same orliweb_color_code_rgt
                    */
                    dispatch({
                        type: 'SET_RECOMMENDATION_REFINEMENTS',
                        payload: [
                            `orliweb_reseau=${RESEAU_H}`,
                            `orliweb_stat10_type_prod=${TYPE_PROD_BAS}`,
                            `orliweb_color_code_rgt=${product?.c_orliweb_color_code_rgt}`,
                            PRODUCT_SEARCH_VARIATION_GROUP_REFINEMENT
                        ]
                    })
                    break
                case ADD_TO_CART_STRATEGIES.IS_FAMILY:
                    /*
                        for products with orliweb_isFamily “MD” or “FS” or “F”, (e.g BILAX461-003)
                        get the products with the same orliweb_isFamily and orliweb_stat20_mat_rgt
                    */
                    dispatch({
                        type: 'SET_RECOMMENDATION_REFINEMENTS',
                        payload: [
                            `orliweb_isFamily=${product?.c_orliweb_isFamily}`,
                            `orliweb_stat20_mat_rgt=${product?.c_orliweb_stat20_mat_rgt}`,
                            PRODUCT_SEARCH_VARIATION_GROUP_REFINEMENT
                        ]
                    })
                    break
                default:
                    // by default the Einstein results based on the product primary category (e.g PKXZ0U51-049)
                    getEinsteinRecommendations('product-to-product', [product])
                    dispatch({type: 'ENABLE_EINSTEIN_RECOMMENDATION'})
                    break
            }
        }
    }, [product, isManualLinkingLoading, manualLinkingResult])

    /*
        If einstein has been used to get recommendations, 
        get the product ids from the einstein recommendations to fetch the data for them in the next steps
    */
    useEffect(() => {
        if (
            recommendationState?.isEinsteinRecommendationEnabled &&
            !isEinsteinRecsLoading &&
            einsteinRecommendations?.recs?.length > 0
        ) {
            dispatch({
                type: 'SET_PRODUCT_IDS_TO_BE_FETCHED',
                payload: einsteinRecommendations?.recs?.map((rec) => rec.id) || []
            })
        }
    }, [
        isEinsteinRecsLoading,
        einsteinRecommendations,
        recommendationState?.isEinsteinRecommendationEnabled
    ])

    /*
        The product search below is being used to get recommendations based on recommendation refinement rules
    */
    const {data: productSearchResult, isLoading: productSearchLoading} = useProductSearch(
        {
            parameters: {
                refine: recommendationState?.recommendationRefinements,
                expand: ['availability'],
                // attribute to check SFS availability of the search hits
                c_sfsAvailability: true
            }
        },
        {
            enabled:
                recommendationState?.isProductSearchEnabled &&
                recommendationState?.recommendationRefinements?.length > 0
        }
    )

    /*
        If a product search call has been used as a recommendation strategy,
        get orderable products from response to fetch only orderable ones
    */
    useEffect(() => {
        if (recommendationState?.isProductSearchEnabled && !productSearchLoading) {
            dispatch({
                type: 'SET_PRODUCT_IDS_TO_BE_FETCHED',
                payload:
                    productSearchResult?.hits
                        ?.filter((hit) => hit.inventory?.orderable || hit.orderable)
                        ?.map((hit) => hit.productId) || []
            })
        }
    }, [productSearchResult, recommendationState?.isProductSearchEnabled, productSearchLoading])

    /*
        Triggered when:
        - Einstein recommendations are selected as a recommendation strategy
        - Recommendations based on the product search are selected as a recommendation strategy
    */
    const {data: useProductsResult, isLoading: isUseProductsLoading} = useProducts(
        {
            parameters: {
                ids: recommendationState?.productIdsToBeFetched?.join(','),
                perPricebook: true,
                allImages: true,
                expand: ['availability', 'promotions', 'variations', 'prices', 'images', 'options']
            }
        },
        {enabled: recommendationState?.productIdsToBeFetched?.length > 0}
    )

    /*
        Update the recommendation results to be returned after useProducts hook has been used 
        and the data has been fetched.
    */
    useEffect(() => {
        if (recommendationState?.productIdsToBeFetched?.length > 0 && !isUseProductsLoading) {
            setAddToCartRecommendationsResult((prevState) => ({
                ...prevState,
                recommendationResult:
                    useProductsResult?.data?.filter((p) => p.inventory?.orderable) || [],
                isRecommendationLoading: isUseProductsLoading
            }))
        }
    }, [recommendationState?.productIdsToBeFetched, useProductsResult, isUseProductsLoading])

    return addToCartRecommendationsResult
}
